import { graphqlPost } from "api/graphqlClient";
import { FormComponentProps } from "components/Form/components/FormComponentProps";
import { useEffect, useMemo, useState } from "react";
import { useForm, useFormState } from "react-final-form";
import {
    buildIncomingReferenceOptions,
    findSelectedIncomingReferenceOption,
    IncomingReferenceCandidateRow,
} from "./incomingReferenceAttributeEditor.utils";
import "./IncomingReferenceAttributeEditor.scss";

/**
 * List every OBJECT_TEMPLATE_REFERENCE attribute (across all templates) whose
 * `reference_object_template_id` is the template being edited — i.e. the valid
 * back-references that point at this template.
 */
const CANDIDATES_QUERY = `query incomingReferenceCandidates($filter: String) {
    object_template_attribute_list(filter: $filter, limit: 500, offset: 0, order: "+name") {
        object_template_attribute_list {
            id
            name
            object_template_id
            object_template_id_object_template {
                name
            }
        }
    }
}`;

/**
 * Editor for an INCOMING_REFERENCE object-template attribute.
 *
 * An INCOMING_REFERENCE attribute shows, on a custom object, the records of some
 * OTHER template that point back at it via one of that template's
 * OBJECT_TEMPLATE_REFERENCE attributes. Two config values describe the relation:
 * `reference_object_template_id` (which referencing template) and
 * `incoming_reference_attribute` (which back-reference attribute on it). Rather
 * than ask the user to fill both by hand, this presents a SINGLE picker of the
 * valid back-references — every OBJECT_TEMPLATE_REFERENCE attribute whose
 * `reference_object_template_id` is THIS template — labelled
 * `<referencing object template name>.<attribute name>`. Picking one settles
 * both config values at once (Stefan's steer, 2026-10-12).
 *
 * The form field value is `incoming_reference_attribute` (the attribute name);
 * the sibling `reference_object_template_id` is set via the final-form API on
 * selection. A native <select> (not a react-select) keeps the control simple and
 * inheriting the canonical edit-field style.
 */
export const IncomingReferenceAttributeEditor = ({
    input,
    fieldProps,
    fieldAttr,
    meta,
}: FormComponentProps) => {
    const form = useForm();
    const { values } = useFormState({ subscription: { values: true } });
    const currentObjectTemplateId: string | undefined = values?.object_template_id;
    const referenceObjectTemplateId: string | undefined = values?.reference_object_template_id;

    const isReadOnly = Boolean(fieldProps?.isReadOnly || fieldAttr?.disabled);
    const label = fieldProps?.label ?? "Incoming reference";
    const hasError = Boolean((fieldProps?.validateOnMount || meta?.touched) && meta?.error);

    const [candidates, setCandidates] = useState<IncomingReferenceCandidateRow[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!currentObjectTemplateId) {
            setCandidates([]);
            return;
        }
        let cancelled = false;
        setIsLoading(true);
        const filter = JSON.stringify({
            and: [
                {
                    field: "reference_object_template_id",
                    op: "eq",
                    value: currentObjectTemplateId,
                },
                { field: "datatype", op: "eq", value: "OBJECT_TEMPLATE_REFERENCE" },
            ],
        });
        graphqlPost({ query: CANDIDATES_QUERY, variables: { filter }, allow_normal: true })
            .then((response: any) => {
                if (cancelled) {
                    return;
                }
                const rows =
                    response?.data?.object_template_attribute_list
                        ?.object_template_attribute_list ?? [];
                setCandidates(rows.filter((row: any) => row?.id));
            })
            .catch(() => {
                if (!cancelled) {
                    setCandidates([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [currentObjectTemplateId]);

    const options = useMemo(() => buildIncomingReferenceOptions(candidates), [candidates]);
    const selected = findSelectedIncomingReferenceOption(
        options,
        input.value,
        referenceObjectTemplateId
    );

    const onSelect = (optionValue: string) => {
        const option = options.find((candidate) => candidate.value === optionValue);
        if (!option) {
            input.onChange(null);
            form.change("reference_object_template_id", null);
            input.onBlur?.();
            return;
        }
        // One selection settles BOTH config values.
        input.onChange(option.attributeName);
        form.change("reference_object_template_id", option.objectTemplateId);
        input.onBlur?.();
    };

    const renderControl = () => {
        if (!currentObjectTemplateId) {
            return (
                <div className="incomingReferenceAttributeEditor__note">
                    An <strong>incoming reference</strong> shows the records of another template
                    that point back at this one. Save this object template first, then choose which
                    records point back at it. Until then there is nothing to choose, so this
                    attribute cannot be created yet — that is why the confirm button stays disabled.
                </div>
            );
        }
        if (isLoading) {
            return <div className="incomingReferenceAttributeEditor__note">Loading…</div>;
        }
        if (options.length === 0) {
            return (
                <div className="incomingReferenceAttributeEditor__note">
                    An <strong>incoming reference</strong> shows the records of another template
                    that point back at this one. No other object template references this one yet,
                    so there is nothing to choose and this attribute cannot be created yet — that is
                    why the confirm button stays disabled. First add an{" "}
                    <strong>object template reference</strong> attribute on another template,
                    pointing at this template; then come back and choose it here.
                </div>
            );
        }
        return (
            <>
                <select
                    name={input.name}
                    aria-label={label}
                    data-testid={input.name}
                    className="input__field"
                    disabled={isReadOnly}
                    value={selected?.value ?? ""}
                    onChange={(event) => onSelect(event.target.value)}
                >
                    <option value="">— Choose an incoming reference —</option>
                    {options.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                <div className="incomingReferenceAttributeEditor__help">
                    Each option is an <strong>object template reference</strong> attribute on another
                    template that points at this one, labelled{" "}
                    <strong>&lt;object template&gt;.&lt;attribute&gt;</strong>. Choosing one sets both
                    the referencing template and its back-reference attribute.
                </div>
            </>
        );
    };

    // All explanatory text is rendered contextually inside renderControl (a
    // "pick one" help line only when there is a list, a "there are none" message
    // otherwise), so the field config carries no static fieldNote that could
    // contradict the current control state.
    return (
        <div
            className={`incomingReferenceAttributeEditor${
                hasError ? " incomingReferenceAttributeEditor--error" : ""
            }`}
            data-testid={`${input.name}-editor`}
        >
            <div className="incomingReferenceAttributeEditor__label">{label}</div>
            <div className="incomingReferenceAttributeEditor__control">{renderControl()}</div>
            {hasError && (
                <div className="incomingReferenceAttributeEditor__errorText">{meta.error}</div>
            )}
        </div>
    );
};
